import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useState } from 'react';
import { useCancelCheck, type CheckTransaction } from '@/hooks/queries/useChecks';
import { toast } from '@/components/ui/toast/useToast';
import { Ban } from 'lucide-react';

interface CancelCheckModalProps {
  open: boolean;
  onClose: () => void;
  check: CheckTransaction | null;
}

export function CancelCheckModal({ open, onClose, check }: CancelCheckModalProps) {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cancelCheck = useCancelCheck();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!check) return;

    setIsSubmitting(true);

    try {
      await cancelCheck.mutateAsync({
        checkId: check.id,
        reason: reason || undefined,
      });

      toast.success(`Le chèque ${check.check_number} a été annulé.`);

      setReason('');
      onClose();
    } catch (error) {
      console.error('Error canceling check:', error);
      toast.error('Impossible d\'annuler le chèque. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!check) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-700">
            <Ban className="w-6 h-6" />
            Annuler le chèque
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">N° Chèque:</span>
              <span className="font-mono font-semibold">{check.check_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">
                {check.type === 'income' ? 'Tireur:' : 'Bénéficiaire:'}
              </span>
              <span className="font-medium">{check.check_drawer_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Montant:</span>
              <span className="text-lg font-bold text-gray-900">
                {formatCurrency(check.amount)}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Raison de l'annulation (optionnel)</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Chèque perdu, transaction annulée, erreur de saisie..."
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Retour
            </Button>
            <Button
              type="submit"
              variant="secondary"
              className="flex-1"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Annulation...' : '⛔ Confirmer l\'annulation'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
